import { Box, Flex, GridItem, Heading, HStack, Spacer, Stack, Tag, Text } from '@chakra-ui/react'
import {
  faCheckCircle,
  faChevronRight,
  faExclamationTriangle,
  faInfoCircle,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import { PrimaryTrainingType, TutorTrainingSubmissionAssignmentPriority } from '@/queries/constants'
import { getPointsForUserEvent } from '@/client/services/progression'

import PointsTag from '@/components/Achievements/PointsTag'

import { SECONDARY_TRAINING_TYPE_TO_NAME } from './common'
import type { TrainingUI } from './types'

export default function TrainingCard({
  training,
  onSelect,
  colSpan,
}: {
  training: TrainingUI
  onSelect: (value: TrainingUI) => void
  colSpan?: number[]
}): JSX.Element {
  let taglineIcon: IconDefinition | null = null
  let taglineToUse = training.tagline ?? 'Optional'
  let taglineColor = 'fg.muted'
  if (training.tagline != null) {
    taglineColor = 'purple.fg'
  }

  if (training.primaryType === PrimaryTrainingType.UNSUSPEND) {
    taglineColor = 'fg.error'
    taglineToUse = 'Required to recover your account'
    taglineIcon = faInfoCircle
  }
  if (
    training.submission?.assignmentPriority === TutorTrainingSubmissionAssignmentPriority.REQUIRED
  ) {
    taglineColor = 'fg.error'
    taglineToUse = 'Required to host new sessions'
    taglineIcon = faInfoCircle
  }
  if (
    training.submission?.assignmentPriority ===
    TutorTrainingSubmissionAssignmentPriority.HIGHLY_RECOMMENDED
  ) {
    taglineColor = 'fg.warning'
    taglineToUse = 'Highly Recommended'
    taglineIcon = faExclamationTriangle
  }
  if (training.submission?.submittedAt != null) {
    taglineColor = 'fg.success'
    taglineToUse = 'Completed'
    taglineIcon = faCheckCircle
  }

  const points = getPointsForUserEvent({
    type: 'tutor_training_completed',
    payload: { trainingId: -1 },
  })

  return (
    <GridItem
      colSpan={colSpan}
      p={7}
      pt={5}
      _hover={{
        backgroundColor: 'bg.subtle',
        border: `2px solid ${chakraTokenToVar('border.info')}`,
      }}
      display="flex"
      flexDirection="column"
      cursor="pointer"
      borderRadius="8px"
      border={`2px solid ${chakraTokenToVar('border')}`}
      bg="bg"
    >
      <Stack
        spacing={4}
        direction="column"
        width="100%"
        onClick={() => onSelect(training)}
        flex={1}
      >
        <Text variant="eyebrow" color={taglineColor}>
          {taglineIcon != null ? <FontAwesomeIcon icon={taglineIcon} /> : null} {taglineToUse}
        </Text>
        <Heading size="lg" as="h4">
          {training.title}
        </Heading>
        <Text noOfLines={3}>{training.excerpt}</Text>
        <Spacer />
        <Flex flex={1} direction="row" align="center" justify="space-between">
          <HStack spacing={2}>
            <Tag>{SECONDARY_TRAINING_TYPE_TO_NAME[training.secondaryType]}</Tag>
            {training.submission?.submittedAt == null ? (
              <Text color="fg.muted" fontWeight="semibold">
                {pluralize(training.timeEstimateMinutes, 'min')}
              </Text>
            ) : (
              <>
                <Text fontWeight="bold" color="primary.fg">
                  Review
                </Text>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  size="sm"
                  color={chakraTokenToVar('primary.fg')}
                />
              </>
            )}
          </HStack>
          {training.primaryType === PrimaryTrainingType.TUTOR &&
          training.submission?.submittedAt == null ? (
            <Box>
              <PointsTag points={points} />
            </Box>
          ) : null}
        </Flex>
      </Stack>
    </GridItem>
  )
}
